import { Link } from "react-router-dom";
import { getHotMovies } from "@/lib/movie";
import { genres } from "@/mocks/movies";

export default function HotSection() {
  const hot = getHotMovies(6);

  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
                Most Watched
              </p>
              <h2 className="mt-2 text-xl font-bold text-foreground-950 md:text-2xl">热门推荐</h2>
            </div>
            <Link
              to="/category?sort=hot"
              className="whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-700"
            >
              热度榜全部
              <i className="ri-arrow-right-line ml-1 text-sm w-4 h-4 inline-flex items-center justify-center" />
            </Link>
          </div>

          <div className="mt-5 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {hot.map((movie, index) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex items-center gap-3 p-3 transition-colors hover:bg-primary-50/60 md:gap-4 md:p-4"
              >
                <span
                  className={`w-6 shrink-0 text-center font-label text-lg font-bold md:w-8 md:text-xl ${
                    index < 3 ? "text-primary-500" : "text-foreground-300"
                  }`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="h-[76px] w-[54px] shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[88px] md:w-[62px]">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 封面`}
                    title={`${movie.title} 高清在线观看`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-medium text-foreground-950 group-hover:text-primary-700 md:text-[15px]">
                    {movie.title}
                  </h3>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500 md:text-xs">
                    <span>{movie.year}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300" />
                    <span>{movie.region}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300" />
                    <span>{movie.genres.slice(0, 2).join(" / ")}</span>
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {movie.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] text-secondary-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="hidden shrink-0 text-right sm:block">
                  <p className="font-label text-lg font-bold text-accent-600">
                    {movie.rating.toFixed(1)}
                  </p>
                  <p className="font-label text-[10px] text-foreground-400">{movie.views} 万播放</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
            <p className="font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
              Weekly Picks
            </p>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950">本周片单</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              编辑从最新上架里挑了三部不同类型的影片，适合在不同心情下打开。
            </p>
            <ul className="mt-4 space-y-3">
              {[
                { label: "想被温柔一下", value: "长夏未央", to: "/detail/endless-summer-2022" },
                { label: "想猜到最后", value: "午夜第七秒", to: "/detail/seventh-second-2025" },
                { label: "想笑一笑", value: "周末喜剧社", to: "/detail/weekend-comedy-club-2024" },
              ].map((item) => (
                <li key={item.value}>
                  <Link
                    to={item.to}
                    className="flex items-center justify-between gap-3 rounded-md bg-background-50/80 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-50 hover:text-primary-700"
                  >
                    <span className="text-xs text-foreground-500">{item.label}</span>
                    <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                      {item.value}
                      <i className="ri-arrow-right-up-line text-sm w-4 h-4 flex items-center justify-center text-primary-500" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="text-sm font-medium text-foreground-900">快速进入类型</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {genres.map((genre) => (
                <Link
                  key={genre.name}
                  to={`/category?type=${encodeURIComponent(genre.name)}`}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                >
                  {genre.name}
                </Link>
              ))}
            </div>
            <Link
              to="/search"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-900 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-secondary-800"
            >
              <i className="ri-search-line text-sm w-4 h-4 flex items-center justify-center" />
              搜索想看的片子
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-baseline gap-2">
              <span className="font-label text-3xl font-extrabold text-accent-600">24</span>
              <span className="text-xs text-foreground-500">部精选影片在架</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              动作、爱情、悬疑、治愈均有覆盖，片单每周补充。
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}